import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../services/api';
import { PageHeader } from './PageHeader';
import type { SupportTicket, SupportTicketDetail, SupportMessage } from '../types';
import './SupportPage.css';

type View = 'list' | 'chat' | 'new';

export const SupportPage: React.FC = () => {
  const [view, setView] = useState<View>('list');
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [activeTicket, setActiveTicket] = useState<SupportTicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [creating, setCreating] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandledRef = useRef(false);

  const loadTickets = useCallback(async () => {
    try {
      const data = await apiClient.getSupportTickets();
      setTickets(data);
    } catch (error) {
      console.error('Failed to load tickets:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (activeTicket?.messages) {
      setTimeout(scrollToBottom, 100);
    }
  }, [activeTicket?.messages, scrollToBottom]);

  const startPolling = useCallback((ticketId: number) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    pollIntervalRef.current = setInterval(async () => {
      try {
        const data = await apiClient.getSupportTicket(ticketId);
        setActiveTicket(data);
      } catch {
        // silently fail
      }
    }, 5000);
  }, []);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const openTicket = useCallback(async (ticketId: number) => {
    try {
      setLoading(true);
      const data = await apiClient.getSupportTicket(ticketId);
      setActiveTicket(data);
      setView('chat');
      startPolling(ticketId);
    } catch (error) {
      console.error('Failed to load ticket:', error);
    } finally {
      setLoading(false);
    }
  }, [startPolling]);

  // Deep link: auto-open a ticket when arriving with ?ticket=<id>
  // (set by the startapp deep link from a support-reply push notification).
  useEffect(() => {
    if (deepLinkHandledRef.current) return;

    const ticketParam = Number(searchParams.get('ticket'));
    if (!ticketParam) return;

    deepLinkHandledRef.current = true;
    openTicket(ticketParam);

    searchParams.delete('ticket');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, openTicket]);

  const handleBack = useCallback(() => {
    if (view === 'chat' || view === 'new') {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setView('list');
      setActiveTicket(null);
      setMessageText('');
      setNewSubject('');
      setNewMessage('');
      loadTickets();
    }
  }, [view, loadTickets]);

  const handleSendMessage = useCallback(async () => {
    if (!activeTicket || !messageText.trim() || sending) return;

    try {
      setSending(true);
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
      }
      const newMsg = await apiClient.sendSupportMessage(activeTicket.id, messageText.trim());
      setActiveTicket(prev => {
        if (!prev) return prev;
        return { ...prev, messages: [...prev.messages, newMsg] };
      });
      setMessageText('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('error');
      }
    } finally {
      setSending(false);
    }
  }, [activeTicket, messageText, sending]);

  const handleCreateTicket = useCallback(async () => {
    if (!newSubject.trim() || !newMessage.trim() || creating) return;

    try {
      setCreating(true);
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
      }
      const ticket = await apiClient.createSupportTicket(newSubject.trim(), newMessage.trim());
      setActiveTicket(ticket);
      setView('chat');
      setNewSubject('');
      setNewMessage('');
      startPolling(ticket.id);
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }
    } catch (error) {
      console.error('Failed to create ticket:', error);
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('error');
      }
    } finally {
      setCreating(false);
    }
  }, [newSubject, newMessage, creating, startPolling]);

  const handleTextareaResize = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageText(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const dayMs = 86400000;

    if (diff < dayMs) return 'Сегодня';
    if (diff < dayMs * 2) return 'Вчера';
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  };

  const groupMessagesByDate = (messages: SupportMessage[]) => {
    const groups: { date: string; messages: SupportMessage[] }[] = [];
    let currentDate = '';

    for (const msg of messages) {
      const date = new Date(msg.created_at).toLocaleDateString('ru-RU');
      if (date !== currentDate) {
        currentDate = date;
        groups.push({ date: msg.created_at, messages: [msg] });
      } else {
        groups[groups.length - 1].messages.push(msg);
      }
    }
    return groups;
  };

  // List view
  if (view === 'list') {
    return (
      <div className="page-container support-page">
        <PageHeader title="Поддержка" />

        <button
          className="support-new-ticket-btn button--primary"
          onClick={() => setView('new')}
        >
          Новое обращение
        </button>

        {loading ? (
          <div className="support-loading">Загрузка...</div>
        ) : tickets.length === 0 ? (
          <div className="support-empty">
            <h3 className="support-empty-title">Нет обращений</h3>
            <p className="support-empty-text">
              Напишите нам, если у вас возникли вопросы или проблемы
            </p>
          </div>
        ) : (
          <div className="support-ticket-list">
            {tickets.map((ticket) => (
              <button
                key={ticket.id}
                className="support-ticket-item"
                onClick={() => openTicket(ticket.id)}
              >
                <div className="support-ticket-header">
                  <span className="support-ticket-subject">{ticket.subject}</span>
                  <span className={`support-ticket-status support-ticket-status--${ticket.status}`}>
                    {ticket.status === 'open' ? 'Открыт' : 'Закрыт'}
                  </span>
                </div>
                {ticket.last_message && (
                  <div className="support-ticket-preview">
                    <span className="support-ticket-sender">
                      {ticket.last_message.is_admin ? 'Поддержка: ' : 'Вы: '}
                    </span>
                    <span className="support-ticket-text">{ticket.last_message.message}</span>
                  </div>
                )}
                <div className="support-ticket-date">
                  {formatDate(ticket.updated_at)}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // New ticket view
  if (view === 'new') {
    return (
      <div className="page-container support-page">
        <PageHeader title="Новое обращение" onBack={handleBack} />

        <div className="support-new-form">
          <div className="support-form-field">
            <label className="support-form-label">Тема</label>
            <input
              type="text"
              className="support-form-input"
              placeholder="Кратко опишите проблему"
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              maxLength={255}
            />
          </div>

          <div className="support-form-field">
            <label className="support-form-label">Сообщение</label>
            <textarea
              className="support-form-textarea"
              placeholder="Опишите подробнее вашу проблему или вопрос..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              rows={5}
              maxLength={5000}
            />
          </div>

          <button
            className="support-submit-btn button--accent"
            onClick={handleCreateTicket}
            disabled={!newSubject.trim() || !newMessage.trim() || creating}
          >
            {creating ? 'Отправка...' : 'Отправить'}
          </button>
        </div>
      </div>
    );
  }

  // Chat view
  const messageGroups = activeTicket ? groupMessagesByDate(activeTicket.messages) : [];
  const isClosed = activeTicket?.status === 'closed';

  return (
    <div className="page-container support-page support-page--chat">
      <PageHeader title={activeTicket?.subject || 'Чат'} onBack={handleBack} />

      {isClosed && (
        <div className="support-closed-banner">
          Обращение закрыто
        </div>
      )}

      <div className="support-messages-container">
        {loading ? (
          <div className="support-loading">Загрузка...</div>
        ) : (
          <div className="support-messages">
            {messageGroups.map((group, gi) => (
              <div key={gi} className="support-message-group">
                <div className="support-date-divider">
                  <span>{formatDate(group.date)}</span>
                </div>
                {group.messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`support-message ${msg.is_admin ? 'support-message--admin' : 'support-message--user'}`}
                  >
                    <div className="support-message-bubble">
                      <div className="support-message-text">{msg.message}</div>
                      <div className="support-message-time">{formatTime(msg.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {!isClosed && (
        <div className="support-input-bar">
          <textarea
            ref={textareaRef}
            className="support-input-field"
            placeholder="Написать сообщение..."
            value={messageText}
            onChange={handleTextareaResize}
            onKeyDown={handleKeyDown}
            rows={1}
            maxLength={5000}
          />
          <button
            className="support-send-btn"
            onClick={handleSendMessage}
            disabled={!messageText.trim() || sending}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};
